import React, { useMemo, useState } from 'react';
import { FaFacebookF } from 'react-icons/fa';
import {
  buildDefaultFacebookVideoPost,
  createFacebookPublishId,
  getFacebookPublishingErrorMessage,
  publishFacebookSightingVideo,
} from '../utils/FB/facebookPublishing';
import './FacebookVideoPublishModal.css';

const pickFirstSource = (...sources) => sources.find(
  (source) => typeof source === 'string' && source.trim(),
) || '';

export default function FacebookVideoPublishModal({
  entry,
  locationLabel,
  page,
  onClose,
  onPublished,
}) {
  const defaults = useMemo(() => buildDefaultFacebookVideoPost({
    species: entry?.species,
    count: entry?.count,
    locationLabel,
  }), [entry?.count, entry?.species, locationLabel]);
  const [title, setTitle] = useState(defaults.title);
  const [description, setDescription] = useState(defaults.description);
  const [idempotencyKey] = useState(createFacebookPublishId);
  const [status, setStatus] = useState({ state: 'idle', message: '', result: null });
  const isPublishing = status.state === 'publishing';
  const isPublished = status.state === 'published';
  const videoSource = pickFirstSource(entry?.videoUrl, entry?.mediaUrl);

  const handlePublish = async () => {
    if (!title.trim() || isPublishing || isPublished) return;
    setStatus({
      state: 'publishing',
      message: 'Sending the video to Facebook. Facebook will fetch and process the clip…',
      result: null,
    });

    try {
      const result = await publishFacebookSightingVideo({
        parentPath: entry?.meta?.parentPath || '',
        speciesDocPath: entry?.meta?.speciesDocPath || '',
        title: title.trim(),
        description: description.trim(),
        idempotencyKey,
      });
      setStatus({
        state: 'published',
        message: `${result?.pageName || page?.name || 'Facebook'} accepted the video and is processing it.`,
        result,
      });
      onPublished?.(result);
    } catch (error) {
      setStatus({
        state: 'error',
        message: getFacebookPublishingErrorMessage(error),
        result: null,
      });
    }
  };

  return (
    <div
      className="facebookPublishModal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="facebook-publish-title"
    >
      <div className="facebookPublishModal__content">
        <div className="facebookPublishModal__header">
          <span className="facebookPublishModal__icon" aria-hidden="true">
            <FaFacebookF />
          </span>
          <div>
            <span className="facebookPublishModal__eyebrow">Review Facebook post</span>
            <h2 id="facebook-publish-title">Post video to {page?.name || 'Facebook'}</h2>
          </div>
          <button
            type="button"
            className="facebookPublishModal__close"
            onClick={onClose}
            disabled={isPublishing}
            aria-label="Close Facebook post review"
          >
            ×
          </button>
        </div>

        <div className="facebookPublishModal__grid">
          <div className="facebookPublishModal__preview">
            {videoSource ? (
              <video src={videoSource} controls playsInline preload="metadata" />
            ) : (
              <div className="facebookPublishModal__placeholder">Video preview unavailable</div>
            )}
            <div className="facebookPublishModal__destination">
              <span>Posting Page</span>
              <strong>{page?.name || 'Facebook Page'}</strong>
            </div>
          </div>

          <div className="facebookPublishModal__form">
            <label>
              <span>Video title</span>
              <input
                type="text"
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                maxLength={255}
                disabled={isPublishing || isPublished}
              />
            </label>
            <label>
              <span>Caption</span>
              <textarea
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                maxLength={5000}
                rows={7}
                disabled={isPublishing || isPublished}
              />
            </label>
            <p className="facebookPublishModal__note">
              Nothing is posted until you press the orange button below. After that, Facebook may take a few minutes to process a large video.
            </p>
          </div>
        </div>

        {status.message ? (
          <div
            className={`facebookPublishModal__status facebookPublishModal__status--${status.state}`}
            role="status"
          >
            {status.message}
            {isPublished && status.result?.facebookVideoId ? (
              <span>Facebook video ID: {status.result.facebookVideoId}</span>
            ) : null}
          </div>
        ) : null}

        <div className="facebookPublishModal__actions">
          <button
            type="button"
            className="facebookPublishModal__secondary"
            onClick={onClose}
            disabled={isPublishing}
          >
            {isPublished ? 'Done' : 'Cancel'}
          </button>
          {!isPublished ? (
            <button
              type="button"
              className="facebookPublishModal__primary"
              onClick={handlePublish}
              disabled={isPublishing || !title.trim() || !videoSource}
            >
              <FaFacebookF />
              {isPublishing ? 'Posting…' : `Post video to ${page?.name || 'Facebook'}`}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
